import { publicOrigin, serverUrl } from '../platform/origin.ts'
import { onBackOnScreen } from '../platform/visibility.ts'

/** The app's own auth client (M22, docs/STACK.md 9.1-9.3; it replaces supabase-js): the session the server's auth module granted (6.3), kept in
 *  memory and in `localStorage['gk.auth']` (the SessionGrant JSON, the same key as before: two e2e specs read `.access_token`), refreshed before its
 *  access token expires, shared between the tabs of this browser. Every call goes to the club's PUBLIC origin (`platform/origin.ts`: the page's own in
 *  a browser, the configured one in the native app); nothing here decides who may do what, the server does.
 *  Refresh (9.3): every 30 s, and when the network or the screen comes back, when less than 120 s are left: under ONE lock for every tab
 *  (`navigator.locks`, a plain mutex where it does not exist), storage re-read first (another tab may have refreshed), a 15 s timeout, a timeout or a
 *  network error retried ONCE after 1-3 s (the server's 24 h grace hands the same child again for a lost answer, also when the phone comes back hours later). 200 stores; a 401 (or 400) is
 *  definitive: the session is cleared and the person signs in again; anything else (network, timeout, 429, 5xx) is transient and keeps everything. */

export const AUTH_STORAGE_KEY = 'gk.auth'
const CHANNEL_NAME = 'gk-auth'
const LOCK_NAME = 'gk-auth'
/** How often the ticker looks at the token, and how close to its expiry it refreshes. */
export const REFRESH_TICK_MS = 30_000
export const REFRESH_AHEAD_S = 120
/** `accessToken()` refreshes first when less than this is left. */
export const TOKEN_MIN_S = 60
/** Every /auth request gives up after this long (the refresh is retried once after a timeout). */
export const AUTH_TIMEOUT_MS = 15_000

export interface Factor { id: string; factor_type: 'totp'; friendly_name: string | null; status: 'verified' | 'unverified'; created_at: string; updated_at: string }
export interface AuthUser { id: string; email: string | null; factors: Factor[] }
/** The SessionGrant of the auth module (STACK 6.3), as stored. `expires_at` is in seconds. */
export interface Session { access_token: string; token_type: 'bearer'; expires_in: number; expires_at: number; refresh_token: string; user: AuthUser }
export type RefreshOutcome = 'ok' | 'refused' | 'transient'
export type Aal = 'aal1' | 'aal2'
export type LinkKind = 'invite' | 'recovery' | 'kiosk'
/** An /auth call that did not succeed: the server's code (STACK 6.4: `invalid_credentials`, `rate_limited`, `link_invalid`, …), or `network`,
 *  `timeout`, `no_public_origin` (a native build made without the club's address), `not_authenticated` (no session here); `status` 0 when nothing answered. */
export interface AuthFailure { error: string; status: number }
export interface EnrolledFactor { id: string; factor_type: 'totp'; friendly_name: string; secret: string; uri: string }

export interface AuthClient {
  /** The session in memory (read from storage the first time), even with an expired token: the field works offline. */
  getSession(): Session | null
  /** The access token to send: refreshed first when under 60 s are left and the network is there, otherwise what there is (null: signed out). */
  accessToken(): Promise<string | null>
  signIn(email: string, password: string): Promise<{ error?: string; status?: number }>
  refresh(): Promise<RefreshOutcome>
  /** Every session of the login ends (scope global), then this browser forgets it, even offline. */
  signOut(): Promise<void>
  /** A one-time link: any session already stored in this browser (another person's, on a shared office PC) is signed out first (scope local). */
  consumeLink(token: string): Promise<{ linkKind: LinkKind; user: AuthUser } | AuthFailure>
  setPassword(password: string, current?: string): Promise<{ error?: string; status?: number }>
  /** GET /auth/user: the login and its factors (stored with the session). Null when there is no session or no answer. */
  user(): Promise<AuthUser | null>
  enrolFactor(friendlyName: string): Promise<EnrolledFactor | AuthFailure>
  /** A code for a factor: the session becomes aal2 (an unverified factor is verified); the new access token is in place when this resolves. */
  verifyFactor(factorId: string, code: string): Promise<{ error?: string; status?: number }>
  /** The access token's own `aal` (decoded, never verified here: the server verifies). */
  aal(): Aal | null
  onChange(cb: (s: Session | null) => void): () => void
  /** The refresh ticker and the other tabs' news (storage event, BroadcastChannel). Returns its stop. */
  startAutoRefresh(): () => void
}

export interface AuthClientOptions {
  fetchImpl?: typeof fetch
  /** null: no persistence (a private window whose storage throws behaves the same) */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
  now?: () => number
  origin?: () => string | null
  sleep?: (ms: number) => Promise<void>
  random?: () => number
  timeoutMs?: number
  /** the cross-tab lock (default: navigator.locks, else an in-page mutex) */
  lock?: <T>(fn: () => Promise<T>) => Promise<T>
  /** whether the device believes it is online (default: navigator.onLine) */
  online?: () => boolean
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

function normFactor(f: unknown): Factor | null {
  if (!isRecord(f) || typeof f.id !== 'string') return null
  return {
    id: f.id, factor_type: 'totp', friendly_name: typeof f.friendly_name === 'string' ? f.friendly_name : null,
    status: f.status === 'verified' ? 'verified' : 'unverified', created_at: String(f.created_at ?? ''), updated_at: String(f.updated_at ?? ''),
  }
}
function normUser(u: unknown): AuthUser | null {
  if (!isRecord(u) || typeof u.id !== 'string') return null
  return { id: u.id, email: typeof u.email === 'string' ? u.email : null, factors: Array.isArray(u.factors) ? u.factors.map(normFactor).filter((f): f is Factor => !!f) : [] }
}
/** A SessionGrant (or a stored one) as a Session; null when it is not one. */
export function toSession(g: unknown): Session | null {
  if (!isRecord(g) || typeof g.access_token !== 'string' || typeof g.refresh_token !== 'string' || typeof g.expires_at !== 'number') return null
  const user = normUser(g.user)
  if (!user) return null
  return { access_token: g.access_token, token_type: 'bearer', expires_in: typeof g.expires_in === 'number' ? g.expires_in : 0, expires_at: g.expires_at, refresh_token: g.refresh_token, user }
}

/** A JWT's payload, decoded without any check (display and phase only: the server verifies every token it is given). */
export function tokenClaims(jwt: string | null | undefined): Record<string, unknown> | null {
  const part = jwt?.split('.')[1]
  if (!part) return null
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const bin = atob(b64 + '==='.slice((b64.length + 3) % 4))
    const json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))
    const v = JSON.parse(json) as unknown
    return isRecord(v) ? v : null
  } catch { return null }
}
export const aalOf = (s: Session | null): Aal | null => {
  const a = tokenClaims(s?.access_token)?.aal
  return a === 'aal1' || a === 'aal2' ? a : null
}
export const hasVerifiedFactor = (u: AuthUser | null | undefined): boolean => !!u?.factors.some((f) => f.status === 'verified')

function defaultStorage(): AuthClientOptions['storage'] {
  try { return typeof localStorage === 'undefined' ? null : localStorage } catch { return null }
}
function defaultLock(): <T>(fn: () => Promise<T>) => Promise<T> {
  let chain: Promise<unknown> = Promise.resolve()
  const mutex = <T,>(fn: () => Promise<T>): Promise<T> => {
    const run = chain.then(fn, fn)
    chain = run.catch(() => undefined)
    return run
  }
  return <T,>(fn: () => Promise<T>): Promise<T> => {
    const locks = typeof navigator === 'undefined' ? undefined : (navigator as Navigator & { locks?: LockManager }).locks
    if (locks && typeof locks.request === 'function') return locks.request(LOCK_NAME, () => fn()) as Promise<T>
    return mutex(fn)
  }
}

type Answer = { status: number; json: unknown; error?: undefined } | { status: 0; json: null; error: 'network' | 'timeout' | 'no_public_origin' }
const codeOf = (a: Answer): string => a.error ?? (isRecord(a.json) && typeof a.json.error === 'string' ? a.json.error : `http_${a.status}`)

export function createAuthClient(o: AuthClientOptions = {}): AuthClient {
  const fetchImpl = o.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a))
  const storage = o.storage === undefined ? defaultStorage() : o.storage
  const now = o.now ?? Date.now
  const origin = o.origin ?? publicOrigin
  const sleep = o.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const random = o.random ?? Math.random
  const timeoutMs = o.timeoutMs ?? AUTH_TIMEOUT_MS
  const lock = o.lock ?? defaultLock()
  const online = o.online ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false)
  const nowSec = () => Math.floor(now() / 1000)

  let loaded = false
  let memory: Session | null = null
  const listeners = new Set<(s: Session | null) => void>()
  const channels = new Set<BroadcastChannel>()

  function readStorage(): Session | null | undefined {           // undefined: storage unusable (keep what is in memory)
    if (!storage) return undefined
    try { const raw = storage.getItem(AUTH_STORAGE_KEY); return raw ? toSession(JSON.parse(raw)) : null } catch { return undefined }
  }
  function load(): Session | null {
    if (!loaded) { loaded = true; memory = readStorage() ?? null }
    return memory
  }
  function notify() { for (const l of [...listeners]) { try { l(memory) } catch { /* a listener's failure is its own */ } } }
  function broadcast() { for (const c of channels) { try { c.postMessage('changed') } catch { /* closed */ } } }
  function store(s: Session) {
    load()
    memory = s
    try { storage?.setItem(AUTH_STORAGE_KEY, JSON.stringify(s)) } catch { /* private mode: memory only */ }
    broadcast(); notify()
  }
  function clear() {
    load()
    const had = memory !== null
    memory = null
    try { storage?.removeItem(AUTH_STORAGE_KEY) } catch { /* ignore */ }
    if (had) { broadcast(); notify() }
  }
  /** Another tab changed the stored session: take it (a sign-out there signs this tab out too). */
  function adoptStored() {
    const stored = readStorage()
    if (stored === undefined) return
    load()
    if (JSON.stringify(stored) === JSON.stringify(memory)) return
    memory = stored
    notify()
  }

  async function request(method: 'GET' | 'POST', path: string, body?: unknown, bearer?: string): Promise<Answer> {
    const url = serverUrl(path, origin())
    if (!url) return { status: 0, json: null, error: 'no_public_origin' }
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
      const headers: Record<string, string> = {}
      if (body !== undefined) headers['content-type'] = 'application/json'
      if (bearer) headers.authorization = `Bearer ${bearer}`
      const r = await fetchImpl(url, { method, headers, ...(body !== undefined ? { body: JSON.stringify(body) } : {}), signal: ctrl.signal, credentials: 'omit', cache: 'no-store' })
      const text = await r.text()
      let json: unknown = null
      if (text) { try { json = JSON.parse(text) } catch { json = null } }
      return { status: r.status, json }
    } catch {
      return { status: 0, json: null, error: ctrl.signal.aborted ? 'timeout' : 'network' }
    } finally { clearTimeout(timer) }
  }

  async function refresh(): Promise<RefreshOutcome> {
    const startedWith = load()?.refresh_token ?? null
    return lock(async () => {
      adoptStored()                                                      // another tab may have refreshed (or signed out) meanwhile
      const s = memory
      if (!s) return 'refused'
      if (s.refresh_token !== startedWith && s.expires_at - nowSec() >= TOKEN_MIN_S) return 'ok'
      let a = await request('POST', '/auth/refresh', { refresh_token: s.refresh_token })
      if (a.error === 'network' || a.error === 'timeout') {              // once, at once: a lost answer gets the same child within the server's grace
        await sleep(1000 + Math.floor(random() * 2000))
        a = await request('POST', '/auth/refresh', { refresh_token: s.refresh_token })
      }
      if (a.status === 200) {
        const next = toSession(a.json)
        if (!next) return 'transient'
        if (memory?.refresh_token !== s.refresh_token) return 'ok'        // signed out or replaced while the request ran: never resurrect
        store(next)
        return 'ok'
      }
      if (a.status === 401 || a.status === 400) {
        if (memory?.refresh_token === s.refresh_token) clear()
        return 'refused'
      }
      return 'transient'
    })
  }

  async function accessToken(): Promise<string | null> {
    const s = load()
    if (!s) return null
    if (s.expires_at - nowSec() < TOKEN_MIN_S && online()) await refresh()
    return memory?.access_token ?? null
  }

  /** A call with the bearer: a 401 refreshes once and tries again (a definitive refusal clears the session). */
  async function authed(method: 'GET' | 'POST', path: string, body?: unknown): Promise<Answer> {
    for (let attempt = 0; ; attempt++) {
      const token = await accessToken()
      if (!token) return { status: 401, json: { error: 'not_authenticated' } }
      const a = await request(method, path, body, token)
      if (a.status !== 401 || attempt > 0) return a
      if ((await refresh()) !== 'ok') return a
    }
  }

  return {
    getSession: load,
    accessToken,
    refresh,
    async signIn(email, password) {
      const a = await request('POST', '/auth/sign-in', { email, password })
      const s = a.status === 200 ? toSession(a.json) : null
      if (s) { store(s); return {} }
      return { error: a.status === 200 ? 'invalid_response' : codeOf(a), status: a.status }
    },
    async signOut() {
      const s = load()
      if (s) await request('POST', '/auth/sign-out', { refresh_token: s.refresh_token, scope: 'global' })
      clear()
    },
    async consumeLink(token) {
      const s = load()
      if (s) { await request('POST', '/auth/sign-out', { refresh_token: s.refresh_token, scope: 'local' }); clear() }
      const a = await request('POST', '/auth/link', { token })
      const next = a.status === 200 ? toSession(a.json) : null
      const kind = isRecord(a.json) ? a.json.link_kind : undefined
      if (!next || (kind !== 'invite' && kind !== 'recovery' && kind !== 'kiosk')) return { error: a.status === 200 ? 'invalid_response' : codeOf(a), status: a.status }
      store(next)
      return { linkKind: kind, user: next.user }
    },
    async setPassword(password, current) {
      const a = await authed('POST', '/auth/password', { password, ...(current ? { current_password: current } : {}) })
      return a.status === 204 ? {} : { error: codeOf(a), status: a.status }
    },
    async user() {
      if (!load()) return null
      const a = await authed('GET', '/auth/user')
      const u = a.status === 200 ? normUser(a.json) : null
      if (!u) return null
      const s = memory
      if (s && s.user.id === u.id && JSON.stringify(s.user) !== JSON.stringify(u)) store({ ...s, user: u })
      return u
    },
    async enrolFactor(friendlyName) {
      const a = await authed('POST', '/auth/factors', { friendly_name: friendlyName })
      const j = a.json
      if (a.status === 200 && isRecord(j) && typeof j.id === 'string' && typeof j.secret === 'string' && typeof j.uri === 'string') {
        return { id: j.id, factor_type: 'totp', friendly_name: typeof j.friendly_name === 'string' ? j.friendly_name : friendlyName, secret: j.secret, uri: j.uri }
      }
      return { error: a.status === 200 ? 'invalid_response' : codeOf(a), status: a.status }
    },
    async verifyFactor(factorId, code) {
      const a = await authed('POST', `/auth/factors/${encodeURIComponent(factorId)}/verify`, { code: code.replace(/\s/g, '') })
      const j = a.json
      const user = a.status === 200 && isRecord(j) ? normUser(j.user) : null
      if (!user || !isRecord(j) || typeof j.access_token !== 'string' || typeof j.expires_at !== 'number') return { error: a.status === 200 ? 'invalid_response' : codeOf(a), status: a.status }
      const s = memory
      if (!s) return { error: 'not_authenticated', status: 401 }
      // an AccessGrant: the server cannot rebuild the current refresh token, so this browser keeps its own (STACK 6.3)
      store({ ...s, access_token: j.access_token, expires_at: j.expires_at, expires_in: typeof j.expires_in === 'number' ? j.expires_in : s.expires_in, user })
      return {}
    },
    aal: () => aalOf(load()),
    onChange(cb) { listeners.add(cb); return () => { listeners.delete(cb) } },
    startAutoRefresh() {
      const tick = () => {
        const s = load()
        if (s && s.expires_at - nowSec() < REFRESH_AHEAD_S && online()) void refresh().catch(() => undefined)
      }
      const timer = setInterval(tick, REFRESH_TICK_MS)
      const onStorage = (e: StorageEvent) => { if (e.key === AUTH_STORAGE_KEY || e.key === null) adoptStored() }
      const win = typeof window === 'undefined' ? null : window
      win?.addEventListener('online', tick)
      win?.addEventListener('storage', onStorage)
      let offScreen: () => void = () => {}
      try { offScreen = onBackOnScreen(tick) } catch { /* no document */ }
      let channel: BroadcastChannel | null = null
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          channel = new BroadcastChannel(CHANNEL_NAME)
          channel.onmessage = () => adoptStored()
          channels.add(channel)
        }
      } catch { channel = null }
      tick()
      return () => {
        clearInterval(timer)
        win?.removeEventListener('online', tick)
        win?.removeEventListener('storage', onStorage)
        offScreen()
        if (channel) { channels.delete(channel); try { channel.close() } catch { /* closed */ } }
      }
    },
  }
}

/** One client per page. */
export const authClient: AuthClient = createAuthClient()
